import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { rupiah, fmtDate } from "../lib/format";
import { RupiahInput } from "../components/RupiahInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, HandCoins, Trash2, Search, History, ReceiptText } from "lucide-react";
import { exportDebtReceiptPDF } from "../lib/pdf";
import { toast } from "sonner";

export default function Debts() {
  const [debts, setDebts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(null);
  const [payForm, setPayForm] = useState(null);
  const [history, setHistory] = useState(null);
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/debts").then((r) => setDebts(r.data));
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Nama wajib diisi"); return; }
    try { await api.post("/debts", form); toast.success("Hutang dicatat"); setForm(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const pay = async () => {
    try {
      const { data } = await api.put(`/debts/${payForm.id}/pay`, { amount: payForm.amount, method: payForm.method });
      toast.success("Pembayaran dicatat · kuitansi dicetak");
      exportDebtReceiptPDF(data.payment, data.debt);
      setPayForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openHistory = async (debt) => {
    try { const { data } = await api.get(`/debts/${debt.id}/payments`); setHistory({ debt, payments: data }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus data hutang ini?")) return; await api.delete(`/debts/${id}`); load(); };

  const table = (type) => {
    const rows = debts.filter((d) => d.debtor_type === type && d.name.toLowerCase().includes(q.toLowerCase()));
    const totalRemaining = rows.reduce((s, d) => s + Math.max(0, d.remaining || 0), 0);
    const totalPaid = rows.reduce((s, d) => s + (d.paid || 0), 0);
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="border-rose-200 bg-rose-50/50"><CardContent className="p-3"><div className="text-xs text-slate-500">Total Hutang Berjalan</div><div className="text-xl font-bold mono text-rose-600 mt-1" data-testid={`${type}-total-remaining`}>{rupiah(totalRemaining)}</div></CardContent></Card>
          <Card className="border-emerald-200 bg-emerald-50/50"><CardContent className="p-3"><div className="text-xs text-slate-500">Total Terbayar</div><div className="text-xl font-bold mono text-emerald-600 mt-1">{rupiah(totalPaid)}</div></CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-3"><div className="text-xs text-slate-500">Jumlah Catatan</div><div className="text-xl font-bold mono text-slate-900 mt-1">{rows.length}</div></CardContent></Card>
        </div>
        <div className="relative max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Cari nama ${type === "employee" ? "karyawan" : "pelanggan"}...`} className="pl-9" data-testid={`${type}-debt-search`} />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Tanggal</TableHead><TableHead>Nama</TableHead><TableHead>Keterangan</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Terbayar</TableHead><TableHead className="text-right">Sisa</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.id} data-testid={`debt-row-${d.id}`}>
                  <TableCell className="text-xs text-slate-500">{fmtDate(d.created_at)}</TableCell>
                  <TableCell className="font-medium">{d.name}</TableCell><TableCell className="text-slate-500 text-sm">{d.description || "-"}</TableCell>
                  <TableCell className="text-right mono">{rupiah(d.amount)}</TableCell>
                  <TableCell className="text-right mono text-emerald-600">{rupiah(d.paid)}</TableCell>
                  <TableCell className="text-right mono font-semibold">{rupiah(d.remaining)}</TableCell>
                  <TableCell><Badge className={d.status === "lunas" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{d.status === "lunas" ? "Lunas" : "Belum Lunas"}</Badge></TableCell>
                  <TableCell className="text-right">
                    {d.status !== "lunas" && <Button size="sm" variant="outline" onClick={() => setPayForm({ id: d.id, amount: d.remaining, max: d.remaining, method: "cash", name: d.name })} data-testid={`pay-debt-${d.id}`}><HandCoins className="h-3.5 w-3.5 mr-1" /> Cicil/Bayar</Button>}
                    <Button size="icon" variant="ghost" onClick={() => openHistory(d)} title="Riwayat Pembayaran" data-testid={`history-debt-${d.id}`}><History className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => del(d.id)}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Tidak ada data</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="debts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900">Buku Hutang</h1>
          <p className="text-sm text-slate-500">Hutang karyawan & pelanggan · cicilan & sisa otomatis (Master & Admin)</p>
        </div>
        <Button onClick={() => setForm({ debtor_type: "customer", name: "", amount: 0, description: "", employee_id: "" })} className="bg-orange-500 hover:bg-orange-600" data-testid="add-debt-button"><Plus className="h-4 w-4 mr-1.5" /> Tambah Hutang</Button>
      </div>

      <Tabs defaultValue="customer" onValueChange={() => setQ("")}>
        <TabsList>
          <TabsTrigger value="customer" data-testid="tab-customer-debt">Hutang Pelanggan</TabsTrigger>
          <TabsTrigger value="employee" data-testid="tab-employee-debt">Hutang Karyawan</TabsTrigger>
        </TabsList>
        <TabsContent value="customer">{table("customer")}</TabsContent>
        <TabsContent value="employee">{table("employee")}</TabsContent>
      </Tabs>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Tambah Hutang</DialogTitle></DialogHeader>
          {form && <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setForm({ ...form, debtor_type: "customer", employee_id: "", name: "" })} className={`py-2.5 rounded-lg border text-sm font-medium ${form.debtor_type === "customer" ? "bg-orange-500 text-white border-orange-500" : "border-slate-200"}`} data-testid="debt-type-customer">Pelanggan</button>
              <button onClick={() => setForm({ ...form, debtor_type: "employee", name: "" })} className={`py-2.5 rounded-lg border text-sm font-medium ${form.debtor_type === "employee" ? "bg-orange-500 text-white border-orange-500" : "border-slate-200"}`} data-testid="debt-type-employee">Karyawan</button>
            </div>
            {form.debtor_type === "employee" ? (
              <div className="space-y-1"><Label>Pilih Karyawan</Label>
                <Select value={form.employee_id || undefined} onValueChange={(v) => { const e = employees.find((x) => x.id === v); setForm({ ...form, employee_id: v, name: e?.name || "" }); }}>
                  <SelectTrigger data-testid="debt-employee-select"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.position}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1"><Label>Nama Pelanggan</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="debt-name-input" /></div>
            )}
            <div className="space-y-1"><Label>Jumlah Hutang</Label><RupiahInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} data-testid="debt-amount-input" /></div>
            <div className="space-y-1"><Label>Keterangan</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>}
          <DialogFooter><Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="save-debt-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payForm} onOpenChange={(o) => !o && setPayForm(null)}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader><DialogTitle>Bayar / Cicil Hutang</DialogTitle></DialogHeader>
          {payForm && <div className="space-y-3">
            <div className="space-y-1"><Label>Metode Pembayaran</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger data-testid="debt-pay-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cash">Tunai</SelectItem><SelectItem value="transfer">Transfer Bank</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Jumlah Bayar</Label>
              <RupiahInput value={payForm.amount} onChange={(v) => setPayForm({ ...payForm, amount: v })} data-testid="debt-pay-input" />
              <p className="text-xs text-slate-500">Sisa hutang: <b className="mono">{rupiah(payForm.max)}</b>. Bisa dicicil sebagian; sisa dihitung otomatis. Kuitansi dicetak otomatis.</p>
            </div>
          </div>}
          <DialogFooter><Button onClick={pay} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-pay-debt-button">Bayar & Cetak Kuitansi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Riwayat Pembayaran — {history?.debt?.name}</DialogTitle></DialogHeader>
          {history && <div className="space-y-2">
            <div className="text-sm text-slate-500">Total: <b className="mono">{rupiah(history.debt.amount)}</b> · Sisa: <b className="mono text-rose-600">{rupiah(history.debt.remaining)}</b></div>
            <div className="rounded-lg border border-slate-200 divide-y max-h-72 overflow-y-auto">
              {history.payments.length === 0 && <div className="p-4 text-center text-slate-400 text-sm">Belum ada pembayaran</div>}
              {history.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`payment-row-${p.id}`}>
                  <div>
                    <div className="font-medium mono text-emerald-600">+{rupiah(p.amount)}</div>
                    <div className="text-[11px] text-slate-500">{fmtDate(p.created_at)} · {p.method === "transfer" ? "Transfer" : "Tunai"}{p.processed_by ? ` · oleh ${p.processed_by}` : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-slate-500">Sisa {rupiah(p.remaining_after)}</div>
                    <Button size="sm" variant="ghost" className="h-6 text-xs text-slate-600" onClick={() => exportDebtReceiptPDF(p, history.debt)}><ReceiptText className="h-3 w-3 mr-1" />Kuitansi</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
